/**
 * useAppMode — what this storefront link is allowed to do, for components.
 *
 * Thin read over `useBranch().appMode`. It exists so call sites express intent
 * as a boolean (`isMenuOnly`) instead of repeating string comparisons, and so
 * the `unresolved` case has one obvious answer everywhere: while config is in
 * flight a link is neither menu-only nor ordering, and mode-dependent chrome
 * stays hidden. That way a digital-menu link never flashes a cart button and an
 * ordering link never flashes a crippled menu.
 */
import { useBranch } from "@/store/branch";
import type { AppMode } from "@/lib/app-mode";

export type UseAppMode = {
  appMode: AppMode;
  /** Ordering is affirmatively disabled: browse + shortlist only. */
  isMenuOnly: boolean;
  /**
   * Ordering is affirmatively enabled. False while `unresolved`, so gate
   * ordering chrome on this rather than on `!isMenuOnly`.
   */
  isOrdering: boolean;
  /** Config has not resolved yet. */
  isResolving: boolean;
};

export function useAppMode(): UseAppMode {
  const { appMode } = useBranch();
  return {
    appMode,
    isMenuOnly: appMode === "menuOnly",
    isOrdering: appMode === "ordering" || appMode === "dineIn",
    isResolving: appMode === "unresolved",
  };
}
